import React, { useEffect, useState } from 'react';
import { Box, Tabs, Tab, Chip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Alert, CircularProgress } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction } from './DataTable';
import TransactionDetailsDrawer from './TransactionDetailsDrawer';
import AmendTransactionDialog from './AmendTransactionDialog';
import CancelTransactionDialog from './CancelTransactionDialog';
import RTGSActionDialog from './RTGSActionDialog';
import { useToast } from './ui/ToastProvider';
import { DEMO_TRANSACTIONS, DemoTransaction } from '../utils/demoTransactions';
import { MOCK_FAILED_TRANSACTIONS } from './FailedTransactionsTable';
import { transactionStore } from '../utils/transactionStore';
interface FailedTabItem {
  id: string;
  label: string;
  stateMatch: string;
  icon: string;
  actions: ('amend' | 'cancel' | 'retry' | 'rtgs' | 'markSuccess')[];
}
const TABS: FailedTabItem[] = [{
  id: 'download',
  label: 'Download',
  stateMatch: 'DOWNLOAD',
  icon: 'lucide:download',
  actions: ['cancel', 'retry']
}, {
  id: 'screening',
  label: 'Screening',
  stateMatch: 'SCREENING',
  icon: 'lucide:shield',
  actions: ['cancel', 'retry']
}, {
  id: 'title-fetch',
  label: 'Title Fetch',
  stateMatch: 'TITLE_FETCH',
  icon: 'lucide:user-search',
  actions: ['amend', 'cancel', 'retry', 'rtgs']
}, {
  id: 'balance-check',
  label: 'Balance Check',
  stateMatch: 'BALANCE_CHECK',
  icon: 'lucide:wallet',
  actions: ['cancel', 'retry', 'rtgs']
}, {
  id: 'ft-post',
  label: 'FT Post',
  stateMatch: 'FT_POST',
  icon: 'lucide:send',
  actions: ['cancel', 'retry', 'rtgs']
}, {
  id: 'raast-post',
  label: 'RAAST Post',
  stateMatch: 'RAAST_POST',
  icon: 'lucide:zap',
  actions: ['cancel', 'retry']
}, {
  id: 'onelink',
  label: '1Link Timeout',
  stateMatch: 'ONELINK_POST',
  icon: 'lucide:link',
  actions: ['cancel', 'retry', 'markSuccess']
}, {
  id: 'rtgs-post',
  label: 'RTGS Post',
  stateMatch: 'RTGS_POST',
  icon: 'lucide:building-2',
  actions: ['cancel', 'rtgs']
}];
export function FailedTabsContent() {
  const [activeTab, setActiveTab] = useState(0);
  const [allTransactions, setAllTransactions] = useState<any[]>([]);
  const [storeUpdateCount, setStoreUpdateCount] = useState(0);
  const {
    showToast
  } = useToast();
  // Dialog states
  const [selectedTransaction, setSelectedTransaction] = useState<any | null>(null);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [amendDialogOpen, setAmendDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [rtgsDialogOpen, setRtgsDialogOpen] = useState(false);
  const [markSuccessDialogOpen, setMarkSuccessDialogOpen] = useState(false);
  const [markSuccessComment, setMarkSuccessComment] = useState('');
  const [markSuccessLoading, setMarkSuccessLoading] = useState(false);
  const [markSuccessError, setMarkSuccessError] = useState<string | null>(null);
  const [manualSettleDialogOpen, setManualSettleDialogOpen] = useState(false);
  const [manualSettleReason, setManualSettleReason] = useState('');
  const [manualSettleLoading, setManualSettleLoading] = useState(false);
  const [manualSettleError, setManualSettleError] = useState<string | null>(null);
  // Subscribe to store
  useEffect(() => {
    const unsubscribe = transactionStore.subscribe(() => {
      setStoreUpdateCount(prev => prev + 1);
    });
    return unsubscribe;
  }, []);
  // Build transaction list
  useEffect(() => {
    const demoFailed = DEMO_TRANSACTIONS.filter(t => t.lifecycleStatus === 'FAILED' || t.lifecycleStatus === 'IN_PROGRESS');
    const mockFailed = MOCK_FAILED_TRANSACTIONS as any[];
    const combined = [...demoFailed, ...mockFailed];
    // Deduplicate by id
    const seen = new Set<string>();
    const unique = combined.filter(t => {
      if (seen.has(t.id)) return false;
      seen.add(t.id);
      return true;
    });
    // Apply store overrides
    const updated = unique.map(txn => {
      const override = transactionStore.getTransactionOverride(txn.id);
      if (override) {
        return {
          ...txn,
          amendStatus: override.amendStatus,
          ...(override.lifecycleStatus && {
            lifecycleStatus: override.lifecycleStatus
          })
        };
      }
      const amendStatus = transactionStore.getAmendmentStatusForTransaction(txn.id);
      if (amendStatus !== 'NONE') {
        return {
          ...txn,
          amendStatus
        };
      }
      return txn;
    }).filter(txn => {
      const rtgsOverride = transactionStore.getRTGSOverride(txn.id);
      if (rtgsOverride) return false;
      const override = transactionStore.getTransactionOverride(txn.id);
      if (override && override.amendStatus === 'APPROVED' && override.lifecycleStatus === 'IN_PROGRESS') return false;
      // Filter out transactions that have a pending or approved manual settlement
      if (override && (override.manualSettlementStatus === 'PENDING_CHECKER' || override.manualSettlementStatus === 'APPROVED')) return false;
      const repushed = transactionStore.getRepushedTransactions();
      if (repushed.some(r => r.id === txn.id)) return false;
      return true;
    });
    setAllTransactions(updated);
  }, [storeUpdateCount]);
  const getTransactionsForTab = (tab: FailedTabItem) => {
    return allTransactions.filter(t => t.currentState === tab.stateMatch);
  };
  const currentTab = TABS[activeTab];
  const tabTransactions = getTransactionsForTab(currentTab);
  // Action handlers
  const handleRetry = (txn: any) => {
    transactionStore.repushTransaction(txn);
    showToast(`Transaction ${txn.id} retried — will resume from ${txn.currentState.replace(/_/g, ' ')}`, 'success');
  };
  const handleAmendConfirm = async (reason: string, newAccount?: string, fetchedTitle?: string) => {
    if (!selectedTransaction) return;
    transactionStore.submitAmendment(selectedTransaction, newAccount || '', fetchedTitle || '', reason);
    showToast(`Amendment request submitted for ${selectedTransaction.id}. Sent to checker for approval.`, 'success');
    setAmendDialogOpen(false);
    setSelectedTransaction(null);
  };
  const handleCancelConfirm = async (reason: string) => {
    if (!selectedTransaction) return;
    showToast(`Cancellation request submitted for ${selectedTransaction.id}`, 'success');
    setCancelDialogOpen(false);
    setSelectedTransaction(null);
  };
  const handleRTGSConfirm = (notes?: string) => {
    if (!selectedTransaction) return;
    transactionStore.addToRTGS(selectedTransaction, notes);
    showToast(`Transaction ${selectedTransaction.id} moved to RTGS`, 'success');
    setRtgsDialogOpen(false);
    setSelectedTransaction(null);
  };
  const handleMarkSuccessConfirm = async () => {
    if (!selectedTransaction) return;
    if (!markSuccessComment.trim()) {
      setMarkSuccessError('Comment is required to mark transaction as successful');
      return;
    }
    setMarkSuccessLoading(true);
    setMarkSuccessError(null);
    await new Promise(resolve => setTimeout(resolve, 800));
    // Remove from list by repushing with success override
    transactionStore.repushTransaction(selectedTransaction);
    showToast(`Transaction ${selectedTransaction.id} marked as successful`, 'success');
    setMarkSuccessLoading(false);
    setMarkSuccessDialogOpen(false);
    setMarkSuccessComment('');
    setSelectedTransaction(null);
  };
  const handleManualSettleConfirm = async () => {
    if (!selectedTransaction) return;
    if (!manualSettleReason.trim()) {
      setManualSettleError('Reason is required to submit for manual settlement');
      return;
    }
    setManualSettleLoading(true);
    setManualSettleError(null);
    await new Promise(resolve => setTimeout(resolve, 800));
    transactionStore.submitManualSettlement(selectedTransaction, manualSettleReason);
    showToast(`Manual settlement request submitted for ${selectedTransaction.id}`, 'success');
    setManualSettleLoading(false);
    setManualSettleDialogOpen(false);
    setManualSettleReason('');
    setSelectedTransaction(null);
  };
  const buildRowActions = (tab: FailedTabItem): RowAction<any>[] => {
    const actions: RowAction<any>[] = [{
      icon: 'lucide:eye',
      label: 'View Details',
      onClick: row => {
        setSelectedTransaction(row);
        setDetailsDrawerOpen(true);
      }
    }];
    if (tab.actions.includes('amend')) {
      actions.push({
        icon: 'lucide:edit',
        label: 'Amend',
        onClick: row => {
          setSelectedTransaction(row);
          setAmendDialogOpen(true);
        },
        condition: (row: any) => (row.payoutType === 'FT' || row.payoutType === 'IBFT') && row.lifecycleStatus === 'FAILED' && (!row.amendStatus || row.amendStatus === 'NONE' || row.amendStatus === 'REJECTED')
      });
    }
    if (tab.actions.includes('cancel')) {
      actions.push({
        icon: 'lucide:x-circle',
        label: 'Cancel',
        onClick: row => {
          setSelectedTransaction(row);
          setCancelDialogOpen(true);
        }
      });
    }
    if (tab.actions.includes('retry')) {
      actions.push({
        icon: 'lucide:rotate-cw',
        label: 'Retry',
        onClick: row => handleRetry(row)
      });
    }
    if (tab.actions.includes('rtgs')) {
      actions.push({
        icon: 'lucide:arrow-right-circle',
        label: 'Move to RTGS',
        onClick: row => {
          setSelectedTransaction(row);
          setRtgsDialogOpen(true);
        }
      });
    }
    if (tab.actions.includes('markSuccess')) {
      actions.push({
        icon: 'lucide:check-circle-2',
        label: 'Mark Success',
        onClick: row => {
          setSelectedTransaction(row);
          setMarkSuccessDialogOpen(true);
        }
      });
      // Add Manually Settle action for duplicate transactions
      actions.push({
        icon: 'lucide:stamp',
        label: 'Manually Settle',
        onClick: row => {
          setSelectedTransaction(row);
          setManualSettleDialogOpen(true);
        },
        condition: (row: any) => {
          // Check if it failed with DUPLICATE_TXN
          const hasDuplicateError = row.stateHistory?.some((h: any) => h.failReasonCode === 'DUPLICATE_TXN');
          // Check if it doesn't already have a pending/approved settlement
          const override = transactionStore.getTransactionOverride(row.id);
          const isNotPending = !override || override.manualSettlementStatus !== 'PENDING_CHECKER' && override.manualSettlementStatus !== 'APPROVED';
          return hasDuplicateError && isNotPending;
        }
      });
    }
    return actions;
  };
  const columns: Column<any>[] = [{
    id: 'id',
    label: 'Txn ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-953">
          {row.id}
        </Typography>
  }, {
    id: 'mtoName',
    label: 'MTO',
    renderCell: row => <Box sx={{
      minWidth: 130
    }} data-id="element-954">
          {row.mtoName ? <Typography variant="body2" fontWeight={500} noWrap data-id="element-955">
              {row.mtoName}
            </Typography> : <Chip label="No MTO" size="small" sx={{
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        color: '#EF4444',
        fontWeight: 600,
        fontSize: '0.7rem'
      }} data-id="element-956" />}
        </Box>
  }, {
    id: 'payoutType',
    label: 'Type',
    renderCell: row => <Chip label={row.payoutType} size="small" sx={{
      backgroundColor: row.payoutType === 'FT' ? 'rgba(14, 165, 233, 0.1)' : row.payoutType === 'IBFT' ? 'rgba(139, 92, 246, 0.1)' : 'rgba(245, 158, 11, 0.1)',
      color: row.payoutType === 'FT' ? '#0EA5E9' : row.payoutType === 'IBFT' ? '#8B5CF6' : '#F59E0B',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-957" />
  }, {
    id: 'amountFCY',
    label: 'Amount',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap sx={{
      minWidth: 100
    }} data-id="element-958">
          {row.currencyFCY} {row.amountFCY}
        </Typography>
  }, {
    id: 'lifecycleStatus',
    label: 'Status',
    renderCell: row => {
      const isFailed = row.lifecycleStatus === 'FAILED';
      return <Chip label={row.lifecycleStatus?.replace(/_/g, ' ')} size="small" sx={{
        backgroundColor: isFailed ? 'rgba(239, 68, 68, 0.1)' : 'rgba(59, 130, 246, 0.1)',
        color: isFailed ? '#EF4444' : '#3B82F6',
        fontWeight: 600,
        fontSize: '0.7rem'
      }} data-id="element-959" />;
    }
  }, {
    id: 'beneficiaryName',
    label: 'Beneficiary',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap sx={{
      maxWidth: 180
    }} data-id="element-960">
          {row.beneficiaryName}
        </Typography>
  }, {
    id: 'createdAt',
    label: 'Created',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-961">
          {new Date(row.createdAt).toLocaleString()}
        </Typography>
  }];
  return <Box data-id="element-962">
      <Box sx={{
      borderBottom: 1,
      borderColor: 'divider',
      mb: 2
    }} data-id="element-963">
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" sx={{
        '& .MuiTab-root': {
          textTransform: 'none',
          fontWeight: 500,
          minHeight: 48
        }
      }} data-id="element-964">
          {TABS.map(tab => {
          const count = getTransactionsForTab(tab).length;
          return <Tab key={tab.id} label={<Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }} data-id="element-966">
                    <Icon icon={tab.icon} width={16} height={16} data-id="element-967" />
                    <Typography variant="body2" fontWeight={500} data-id="element-968">
                      {tab.label}
                    </Typography>
                    <Chip label={count} size="small" sx={{
              height: 20,
              fontSize: '0.7rem',
              fontWeight: 700,
              backgroundColor: count > 0 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(156, 163, 175, 0.1)',
              color: count > 0 ? '#EF4444' : '#9CA3AF'
            }} data-id="element-969" />
                  </Box>} data-id="element-965" />;
        })}
        </Tabs>
      </Box>

      <DataTable title={`${currentTab.label} — Failed Transactions`} data={tabTransactions} columns={columns} rowActions={buildRowActions(currentTab)} keyField="id" emptyStateMessage={`No failed transactions at ${currentTab.label} stage`} initialVisibleColumns={['id', 'mtoName', 'payoutType', 'amountFCY', 'lifecycleStatus', 'beneficiaryName', 'createdAt']} onRowClick={row => {
      setSelectedTransaction(row);
      setDetailsDrawerOpen(true);
    }} data-id="element-970" />

      {/* Transaction Details Drawer */}
      {selectedTransaction && <TransactionDetailsDrawer open={detailsDrawerOpen} onClose={() => {
      setDetailsDrawerOpen(false);
      setSelectedTransaction(null);
    }} transaction={{
      ...selectedTransaction,
      currentState: selectedTransaction.currentState as any,
      lifecycleStatus: selectedTransaction.lifecycleStatus as any
    } as any} data-id="element-971" />}

      {/* Amend Dialog */}
      {selectedTransaction && <AmendTransactionDialog open={amendDialogOpen} onClose={() => {
      setAmendDialogOpen(false);
      setSelectedTransaction(null);
    }} onConfirm={handleAmendConfirm} transaction={selectedTransaction} data-id="element-972" />}

      {/* Cancel Dialog */}
      {selectedTransaction && <CancelTransactionDialog open={cancelDialogOpen} onClose={() => {
      setCancelDialogOpen(false);
      setSelectedTransaction(null);
    }} onConfirm={handleCancelConfirm} transactionId={selectedTransaction.id} cancellationAction={selectedTransaction.cancellationAction || 'SIMPLE_CANCEL'} data-id="element-973" />}

      {/* RTGS Dialog */}
      {selectedTransaction && <RTGSActionDialog open={rtgsDialogOpen} onClose={() => {
      setRtgsDialogOpen(false);
      setSelectedTransaction(null);
    }} onConfirm={handleRTGSConfirm} transaction={selectedTransaction} action="send" data-id="element-974" />}

      {/* Mark Success Dialog (1Link Timeout only) */}
      <Dialog open={markSuccessDialogOpen} onClose={() => {
      if (!markSuccessLoading) {
        setMarkSuccessDialogOpen(false);
        setMarkSuccessComment('');
        setMarkSuccessError(null);
        setSelectedTransaction(null);
      }
    }} maxWidth="sm" fullWidth data-id="element-975">
        <DialogTitle data-id="element-976">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-977">
            <Icon icon="lucide:check-circle-2" width={24} color="#10B981" data-id="element-978" />
            <Typography variant="h6" fontWeight={600} data-id="element-979">
              Mark as Successful
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-980">
          <Box sx={{
          mt: 1
        }} data-id="element-981">
            {selectedTransaction && <Typography variant="body2" color="text.secondary" sx={{
            mb: 2
          }} data-id="element-982">
                Transaction ID: <strong data-id="element-983">{selectedTransaction.id}</strong>
              </Typography>}
            <Alert severity="info" sx={{
            mb: 2
          }} data-id="element-984">
              <Typography variant="body2" fontWeight={600} data-id="element-985">
                1Link Timeout — Manual Confirmation
              </Typography>
              <Typography variant="body2" sx={{
              mt: 0.5
            }} data-id="element-986">
                This transaction timed out on 1Link. If you have confirmed with
                the beneficiary bank that funds were credited, you can mark this
                transaction as successful.
              </Typography>
            </Alert>
            <TextField label="Confirmation Comment" placeholder="Enter reason / confirmation details for marking as successful..." multiline rows={4} fullWidth required value={markSuccessComment} onChange={e => {
            setMarkSuccessComment(e.target.value);
            setMarkSuccessError(null);
          }} error={!!markSuccessError} helperText={markSuccessError} disabled={markSuccessLoading} data-id="element-987" />
          </Box>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2
      }} data-id="element-988">
          <Button onClick={() => {
          setMarkSuccessDialogOpen(false);
          setMarkSuccessComment('');
          setMarkSuccessError(null);
          setSelectedTransaction(null);
        }} disabled={markSuccessLoading} data-id="element-989">
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={handleMarkSuccessConfirm} disabled={markSuccessLoading || !markSuccessComment.trim()} startIcon={markSuccessLoading ? <CircularProgress size={16} color="inherit" data-id="element-991" /> : <Icon icon="lucide:check" width={16} data-id="element-992" />} data-id="element-990">
            Mark Successful
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manually Settle Dialog */}
      <Dialog open={manualSettleDialogOpen} onClose={() => {
      if (!manualSettleLoading) {
        setManualSettleDialogOpen(false);
        setManualSettleReason('');
        setManualSettleError(null);
        setSelectedTransaction(null);
      }
    }} maxWidth="sm" fullWidth data-id="element-993">
        <DialogTitle data-id="element-994">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-995">
            <Icon icon="lucide:stamp" width={24} color="#0EA5E9" data-id="element-996" />
            <Typography variant="h6" fontWeight={600} data-id="element-997">
              Manually Settle Transaction
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-998">
          <Box sx={{
          mt: 1
        }} data-id="element-999">
            {selectedTransaction && <Typography variant="body2" color="text.secondary" sx={{
            mb: 2
          }} data-id="element-1000">
                Transaction ID: <strong data-id="element-1001">{selectedTransaction.id}</strong>
              </Typography>}
            <Alert severity="info" sx={{
            mb: 3
          }} data-id="element-1002">
              <Typography variant="body2" fontWeight={600} data-id="element-1003">
                1Link Duplicate Transaction
              </Typography>
              <Typography variant="body2" sx={{
              mt: 0.5
            }} data-id="element-1004">
                This transaction failed on 1Link with a duplicate transaction
                error. If you have confirmed the funds were credited, submit for
                manual settlement approval.
              </Typography>
            </Alert>
            <TextField label="Settlement Reason" placeholder="Enter confirmation details for manual settlement..." multiline rows={4} fullWidth required value={manualSettleReason} onChange={e => {
            setManualSettleReason(e.target.value);
            setManualSettleError(null);
          }} error={!!manualSettleError} helperText={manualSettleError} disabled={manualSettleLoading} data-id="element-1005" />
          </Box>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2
      }} data-id="element-1006">
          <Button onClick={() => {
          setManualSettleDialogOpen(false);
          setManualSettleReason('');
          setManualSettleError(null);
          setSelectedTransaction(null);
        }} disabled={manualSettleLoading} data-id="element-1007">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleManualSettleConfirm} disabled={manualSettleLoading || !manualSettleReason.trim()} sx={{
          bgcolor: '#0EA5E9',
          '&:hover': {
            bgcolor: '#0284C7'
          }
        }} startIcon={manualSettleLoading ? <CircularProgress size={16} color="inherit" data-id="element-1009" /> : <Icon icon="lucide:send" width={16} data-id="element-1010" />} data-id="element-1008">
            Submit for Settlement
          </Button>
        </DialogActions>
      </Dialog>
    </Box>;
}
export default FailedTabsContent;